import * as React from "react";
import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { Button } from "components/ui/button";
import { cn } from "@/utils/shadcn-utils";

export type ModalFormProps = {
  open: boolean;
  title?: string;
  onClose: () => void;
  children?: React.ReactNode;
  className?: string;
  hasStepForm?: boolean;
  steps?: string[];
  stepNumber?: number;
  currentStep?: number;
  onPrevStep?: () => void;
  onNextStep?: () => void;
  modalWidth?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl" | "5xl";
  formSubmit?: (data: any) => void;
  cancelButtonLabel?: string | React.ReactNode;
  submitButtonLabel?: string | React.ReactNode;
  submitButtonProps?: any;
  cancelButtonProps?: any;
  withFooter?: boolean;
};

const widthClasses = {
  xs: "max-w-xs",
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
  "5xl": "max-w-5xl",
};

export default function ModalForm({
                                    open,
                                    title,
                                    onClose,
                                    children,
                                    className,
                                    hasStepForm = false,
                                    steps = [],
                                    stepNumber = 0,
                                    currentStep = 0,
                                    onPrevStep,
                                    onNextStep,
                                    modalWidth = "md",
                                    formSubmit,
                                    cancelButtonLabel = "Annuler",
                                    submitButtonLabel = "Enregistrer",
                                    submitButtonProps,
                                    cancelButtonProps,
                                    withFooter = true,
                                  }: ModalFormProps) {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      const handleEsc = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          onClose();
        }
      };

      document.addEventListener("keydown", handleEsc);
      return () => {
        document.body.style.overflow = "unset";
        document.removeEventListener("keydown", handleEsc);
      };
    }
  }, [open, onClose]);

  if (!open) return null;

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only close if clicking directly on the backdrop (not on children)
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const modalContent = (
    <>
      {/* Backdrop - clicking it closes the modal */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Modal Container */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={handleBackdropClick}>
        <div
          className={cn(
            "relative bg-white rounded-lg shadow-xl w-full overflow-hidden",
            widthClasses[modalWidth],
            "max-h-[90vh] flex flex-col",
            className
          )}
          onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between py-4 px-6 border-b border-gray-200 flex-shrink-0">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg p-1 transition-colors">
              <X className="w-5 h-5" />
            </button>
          </div>

          {hasStepForm && (
            <div className="px-6 py-4 border-b border-gray-200 flex-shrink-0">
              <div className="flex items-center justify-between">
                {steps.map((label, index) => (
                  <React.Fragment key={label}>
                    <div className="flex flex-col items-center">
                      <div
                        className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold",
                          index < currentStep
                            ? "bg-blue-600 text-white"
                            : index === currentStep
                            ? "bg-blue-100 text-blue-600 border-2 border-blue-600"
                            : "bg-gray-200 text-gray-500"
                        )}>
                        {index + 1}
                      </div>
                      <span
                        className={cn(
                          "text-xs mt-1",
                          index <= currentStep ? "text-gray-900 font-medium" : "text-gray-500"
                        )}>
                        {label}
                      </span>
                    </div>
                    {index < steps.length - 1 && (
                      <div
                        className={cn("flex-1 h-0.5 mx-2 mb-6", index < currentStep ? "bg-blue-600" : "bg-gray-200")}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-8 py-6">{children}</div>

          {withFooter && (
            <div className="bg-gray-50 py-4 px-6 border-t border-gray-200 flex-shrink-0">
              <div className="flex justify-end gap-3">
                {hasStepForm ? (
                  <>
                    {currentStep === 0 && (
                      <Button variant="outline" onClick={onClose}>
                        Annuler
                      </Button>
                    )}
                    {currentStep > 0 && (
                      <Button variant="outline" onClick={onPrevStep}>
                        Précédent
                      </Button>
                    )}
                    {currentStep < stepNumber && (
                      <Button onClick={onNextStep}>Suivant</Button>
                    )}
                    {currentStep === stepNumber && (
                      <Button onClick={formSubmit}>Enregistrer</Button>
                    )}
                  </>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={onClose}
                      {...cancelButtonProps}>
                      {cancelButtonLabel}
                    </Button>
                    <Button
                      onClick={formSubmit}
                      {...submitButtonProps}
                      className={cn(
                        "bg-blue-800",
                        submitButtonProps?.className ?? "",
                      )}>
                      {submitButtonLabel}
                    </Button>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )

  return createPortal(modalContent, document.body);
}